import React from 'react';
import {
  chakra,
  Skeleton,
  Table,
  TableContainer,
  Tbody,
  Td,
  Th,
  Thead,
  Tr,
} from '@chakra-ui/react';
import {AiFieldDefinition} from '../../../shared/fields/enrichment';
import {ObjectId} from 'bson';
import _ from 'lodash';
import {BuilderPreviewAccountCell} from './BuilderPreviewAccountCell';
import {useEnrichmentPreviewResult} from './hooks/useEnrichmentPreviewResult';
import {BuilderPreviewValue} from './BuilderPreviewValue';

const BuilderPreviewRow = ({
  accountId,
  fieldDefinition,
  isPreviewDisabled = false,
}: {
  accountId: ObjectId;
  fieldDefinition: AiFieldDefinition;
  isPreviewDisabled?: boolean;
}) => {
  const {data, failureCount, isFetching, isPending} =
    useEnrichmentPreviewResult({
      accountId,
      fieldDefinitionId: fieldDefinition.id,
      isDisabled: isPreviewDisabled,
    });

  if (isPending || (isFetching && failureCount > 0)) {
    return (
      <Tr>
        <Td>
          <BuilderPreviewAccountCell accountId={accountId} />
        </Td>
        <Td>
          <Skeleton h="2" speed={0} />
        </Td>
        <Td>
          <Skeleton h="2" speed={0} />
        </Td>
      </Tr>
    );
  }

  const {value, reasoning, stale} = data ?? {};

  return (
    <Tr>
      <Td>
        <BuilderPreviewAccountCell accountId={accountId} />
      </Td>
      <Td whiteSpace="pre-line">
        {data === null ? (
          '-'
        ) : (
          <BuilderPreviewValue value={value} isStale={stale} />
        )}
      </Td>
      <Td>{reasoning}</Td>
    </Tr>
  );
};

const NoDataPreviewRow = ({accountId}: {accountId: ObjectId}) => {
  return (
    <Tr>
      <Td>
        <BuilderPreviewAccountCell accountId={accountId} />
      </Td>
      <Td colSpan={2} textColor="kgray.300">
        -
      </Td>
    </Tr>
  );
};

export const BuilderPreviewTable = ({
  accountIds,
  fieldDefinition,
  isPreviewDisabled = false,
}: {
  accountIds: ObjectId[];
  fieldDefinition?: AiFieldDefinition;
  isPreviewDisabled?: boolean;
}) => {
  return (
    <TableContainer
      bgColor="white"
      borderRadius="md"
      border="1px solid"
      borderColor="gray.200"
      overflowY="auto"
      whiteSpace="normal"
    >
      <Table variant="simple" size="md">
        <colgroup>
          <col width="40%" />
          <col width="20%" />
          <col width="40%" />
        </colgroup>
        <Thead whiteSpace="nowrap">
          <Tr>
            <HeaderCell>Account</HeaderCell>
            <HeaderCell>Value</HeaderCell>
            <HeaderCell>Reasoning</HeaderCell>
          </Tr>
        </Thead>
        <Tbody>
          {accountIds.map((accountId) => {
            return fieldDefinition ? (
              <BuilderPreviewRow
                key={accountId.toString()}
                accountId={accountId}
                fieldDefinition={fieldDefinition}
                isPreviewDisabled={isPreviewDisabled}
              />
            ) : (
              <NoDataPreviewRow accountId={accountId} />
            );
          })}
        </Tbody>
      </Table>
    </TableContainer>
  );
};

const HeaderCell = chakra(Th, {
  baseStyle: {
    bgColor: 'gray.50',
    fontSize: 'sm',
    fontWeight: 'normal',
    position: 'sticky',
    top: 0,
    textColor: 'kgray.500',
  },
});
